/* ============================================================================
   The project's journey, drawn as a timeline the client can actually read —
   every real moment in order: the brief approved, presentations shown, stages
   begun and submitted, drawings issued, sign-offs, payments, the site moving.
   Nothing invented: every dot is a recorded event with its date.
   ========================================================================== */
import { useMemo, useState } from "react";
import { Icon } from "./Icons";
import { fmtDate } from "../lib/format";
import type { Project } from "../lib/types";

interface Moment {
  on: string;                       // ISO date/instant
  title: string;
  detail?: string;
  kind: "start" | "brief" | "design" | "drawings" | "submission" | "approval" | "money" | "site" | "stage";
  image?: string;
}

const KIND_STYLE: Record<Moment["kind"], { color: string; icon: keyof typeof Icon; label: string }> = {
  start: { color: "var(--ink)", icon: "pin", label: "Project" },
  stage: { color: "var(--accent-ink)", icon: "report", label: "Stage" },
  brief: { color: "var(--sky)", icon: "brief", label: "Brief" },
  design: { color: "var(--accent-ink)", icon: "present", label: "Design" },
  drawings: { color: "var(--sage)", icon: "layers", label: "Drawings" },
  submission: { color: "var(--accent-ink)", icon: "send", label: "Submission" },
  approval: { color: "var(--ok, #5c7a3f)", icon: "check", label: "Sign-off" },
  money: { color: "var(--gold)", icon: "money", label: "Billing" },
  site: { color: "var(--ink-2)", icon: "pin", label: "Site" },
};

function collectMoments(p: Project): Moment[] {
  const out: Moment[] = [];
  const push = (on: string | undefined, m: Omit<Moment, "on">) => { if (on) out.push({ on, ...m }); };

  push(p.createdOn, { title: "The project opened", detail: `${p.name} · ${p.location}`, kind: "start" });
  push(p.brief.approvedOn, { title: "You approved the design brief", detail: p.brief.approvedBy ? `Signed off by ${p.brief.approvedBy}` : undefined, kind: "brief" });
  for (const s of p.stages) {
    push(s.startedOn, { title: `${s.label} began`, kind: "stage" });
    push(s.completedOn, { title: `${s.label} completed`, kind: "stage" });
  }
  for (const d of [p.deck, ...(p.deckArchive ?? [])]) {
    if (!d || d.status !== "issued") continue;
    const img = d.options.find((o) => o.images[0])?.images[0];
    push(d.issuedOn, { title: `Presented — ${d.title}`, detail: d.stage, kind: "design", image: img });
    push(d.acceptance?.on, { title: d.acceptance?.status === "accepted" ? `You accepted ${d.title}` : `You asked for changes to ${d.title}`, kind: "design" });
  }
  for (const s of p.submissions ?? []) {
    push(s.submittedOn, { title: s.title, detail: `${s.ref} · ${s.sheets.length} sheets, provided in full`, kind: "submission", image: s.sheets.find((x) => x.previewUrl)?.previewUrl });
  }
  for (const s of (p.drawingSets ?? []).filter((x) => x.sharedWithClient)) {
    push(s.issuedOn, { title: `Drawings issued — ${s.label}`, detail: `${s.sheets.length} sheets`, kind: "drawings", image: s.sheets.find((x) => x.previewUrl)?.previewUrl });
  }
  for (const a of (p.approvals ?? []).filter((x) => x.status === "signed")) {
    push(a.signedOn, { title: `You signed off — ${a.title}`, detail: `${a.sheets.length} sheet${a.sheets.length === 1 ? "" : "s"}`, kind: "approval" });
  }
  push(p.engagement?.signedOn, { title: "Engagement signed", detail: "The appointment formally began", kind: "approval" });
  for (const inv of (p.invoices ?? []).filter((x) => x.status === "paid" && x.paidOn)) {
    push(inv.paidOn, { title: `${inv.ref} settled`, detail: inv.title, kind: "money" });
  }
  for (const u of p.siteLog ?? []) {
    push(u.on, { title: u.title, detail: [u.zone, typeof u.percent === "number" ? `${u.percent}% complete` : undefined, u.note].filter(Boolean).join(" · ").slice(0, 140) || undefined, kind: "site", image: (u as { photos?: string[] }).photos?.[0] });
  }
  return out.sort((a, b) => +new Date(a.on) - +new Date(b.on));
}

export function ClientTimeline({ p }: { p: Project }) {
  const moments = useMemo(() => collectMoments(p), [p]);
  const [showAll, setShowAll] = useState(false);
  if (moments.length < 2) return null;

  const now = Date.now();
  const nextIdx = moments.findIndex((m) => +new Date(m.on) > now);
  const shown = showAll || moments.length <= 12 ? moments : moments.slice(-12);
  const hidden = moments.length - shown.length;

  return (
    <div className="card reveal" style={{ overflow: "hidden", marginBottom: 24 }}>
      <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
        <span className="kicker">The journey so far</span>
        <span className="dim" style={{ fontSize: 12 }}>{moments.length} moments</span>
      </div>
      <div style={{ padding: "18px 18px 20px" }}>
        {hidden > 0 && (
          <button className="chip" style={{ fontSize: 11, marginBottom: 14 }} onClick={() => setShowAll(true)}>
            Show the {hidden} earlier moment{hidden === 1 ? "" : "s"}
          </button>
        )}
        <div style={{ position: "relative", paddingLeft: 30 }}>
          {/* the rail */}
          <div style={{ position: "absolute", left: 9, top: 6, bottom: 6, width: 2, background: "linear-gradient(var(--accent) 0%, var(--line-2) 100%)", borderRadius: 2 }} />
          {shown.map((m, i) => {
            const st = KIND_STYLE[m.kind];
            const Ic = (Icon as Record<string, (p: { width?: number; style?: React.CSSProperties }) => JSX.Element>)[st.icon] ?? Icon.check;
            const isLast = i === shown.length - 1 && nextIdx === -1;
            return (
              <div key={`${m.on}-${i}`} style={{ position: "relative", paddingBottom: i === shown.length - 1 ? 0 : 20 }}>
                {/* the dot */}
                <span style={{
                  position: "absolute", left: -30, top: 2, width: 20, height: 20, borderRadius: 20,
                  display: "grid", placeItems: "center", background: "var(--card)",
                  border: `2px solid ${st.color}`, color: st.color, zIndex: 1,
                  boxShadow: isLast ? `0 0 0 4px color-mix(in srgb, ${st.color} 18%, transparent)` : "none",
                }}>
                  <Ic width={10} />
                </span>
                <div className="row between wrap" style={{ gap: 8, alignItems: "baseline" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, minWidth: 0 }}>{m.title}</span>
                  <span className="dim mono" style={{ fontSize: 10.5, flexShrink: 0 }}>{fmtDate(m.on)}</span>
                </div>
                {m.detail && <div className="dim" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.5, maxWidth: 520 }}>{m.detail}</div>}
                {m.image && (
                  <img src={m.image} alt="" style={{ marginTop: 8, width: "min(220px, 100%)", height: 90, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)", display: "block" }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
